'use client';

// ─────────────────────────────────────────────────────────────────────────────
// QCInspectionPanel
//
// Quality Control daily-route content, rendered as a section inside the
// merged Project Manager dashboard. Previously this lived at its own route
// (/quality-control-dashboard) but PM and QC are the same field role now —
// see migration 20260527120000_consolidate_qc_into_project_manager.sql.
//
// Differences vs. the old QCDashboardInteractive page:
//   - No <div className="min-h-screen"> wrapper, no sticky page header bar.
//     The PM dashboard already owns the page chrome (header, avatar, etc.).
//   - The clock-in pill + messenger button live in this section's own
//     header strip so the QC field worker still has them at hand.
//   - Field-first treatment is scoped to this section only; the existing
//     PM widgets above are not touched.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import { useAuth, type UserRole } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { createClient } from '@/lib/supabase/client';
import CustomerCardPanel from '@/components/common/CustomerCardPanel';
import { mapPipelineRow } from '@/components/common/CustomerCardPanel';
import type { CustomerCard } from '@/components/common/CustomerCardPanel';

// Retry-on-429 / timeout wrapper for the /api/rbac/list-qc-inspections fetch.
// Aborts (Strict Mode unmount) are not retried.
async function fetchListWithRetry(
  payload: { effectiveRoles: UserRole[]; effectiveUserId: string | null; date: string },
  signal: AbortSignal,
  maxAttempts = 3,
  timeoutMs = 8000,
): Promise<Response> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Request timed out (attempt ${attempt + 1}/${maxAttempts})`)),
        timeoutMs,
      ),
    );
    try {
      const res = await Promise.race([
        fetch('/api/rbac/list-qc-inspections', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal,
        }),
        timeoutPromise,
      ]);
      if (res.status === 429 && attempt < maxAttempts - 1) {
        console.warn(`[QCInspections] 429 on attempt ${attempt + 1}/${maxAttempts}, backing off`);
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt))); // 1s, 2s, 4s
        continue;
      }
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if ((err as { name?: string })?.name === 'AbortError' || signal.aborted) throw err;
      const isTimeout = msg.includes('timed out');
      console.warn(`[QCInspections] attempt ${attempt + 1} failed: ${msg}`);
      if (attempt === maxAttempts - 1 || !isTimeout) throw err;
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }
  throw new Error('[QCInspections] exhausted retry attempts');
}

// Dynamically import the map to avoid SSR issues with Leaflet
const QCRouteMap = dynamic(() => import('./QCRouteMap'), { ssr: false, loading: () =>
  <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded-xl">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="font-body text-sm text-muted-foreground">Loading map...</span>
    </div>
  </div>
});

interface QCInspection {
  id: string;
  job_id: string;
  assigned_inspector_id: string;
  address: string;
  lat: number;
  lng: number;
  status: 'Ready for QC' | 'In Progress' | 'Completed';
  homeowner_name: string;
  homeowner_phone: string;
  pm_name: string;
  pm_phone: string;
  foreman_name: string;
  foreman_phone: string;
  notes: string;
  inspection_date: string;
}

interface ChatMessage {
  id: string;
  sender: string;
  text: string;
  time: string;
  isMe: boolean;
}

interface QcMessage {
  id: string;
  job_id: string;
  sender_name: string;
  sender_role: string;
  message: string;
  created_at: string;
}

interface PhotoRecord {
  id: string;
  src: string;
  alt: string;
  job_id?: string;
}

export default function QCInspectionPanel() {
  const { currentUser } = useAuth();
  const { getEffectiveRoles, getEffectiveUserId } = useImpersonation();
  const supabase = createClient();

  const effectiveRoles = getEffectiveRoles(currentUser.roles);
  const effectiveUserId = getEffectiveUserId(currentUser.id || null);

  // RBAC-3 — abort in-flight list-qc-inspections fetch (Strict Mode safety).
  const abortRef = useRef<AbortController | null>(null);

  const [inspections, setInspections] = useState<QCInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState<QCInspection | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Clock-in lives on the Today tab via ClockInBar (one timer per dashboard).
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateNote, setEscalateNote] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  // ── Photos state ──────────────────────────────────────────────────────────
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);

  // SmartPick AI lives on the Today tab via PMSmartPickSummary (which already
  // covers QC scope: "My Jobs · Crew · Materials · QC"). QC + PM escalations
  // are merged into MergedEscalationsPanel on the Tickets tab. This panel
  // is the inspection-route surface only: map + cards + drawer.

  // Unified customer card panel state
  const [selectedPipelineCard, setSelectedPipelineCard] = useState<CustomerCard | null>(null);
  const [loadingPipelineCard, setLoadingPipelineCard] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // ── Load photos from photos table ─────────────────────────────────────────
  const loadPhotos = useCallback(async (jobId?: string) => {
    try {
      let query = supabase.from('photos').select('id, src, alt, job_id').not('src', 'is', null).limit(6);
      if (jobId) query = query.eq('job_id', jobId);
      const { data } = await query;
      if (data && data.length > 0) {
        setPhotos(data.map((p: any) => ({ id: p.id, src: p.src, alt: p.alt || 'Roof inspection photo', job_id: p.job_id })));
      } else {
        setPhotos([]);
      }
    } catch {
      setPhotos([]);
    }
  }, [supabase]);

  // ── Load chat messages from qc_messages ──────────────────────────────────
  const loadMessages = useCallback(async (jobId: string) => {
    try {
      const { data } = await supabase
        .from('qc_messages')
        .select('id, job_id, sender_name, sender_role, message, created_at')
        .eq('job_id', jobId)
        .order('created_at', { ascending: true })
        .limit(20);

      if (data && data.length > 0) {
        const mapped: ChatMessage[] = data.map((m: QcMessage) => ({
          id: m.id,
          sender: `${m.sender_name} (${m.sender_role.replace(/_/g, ' ')})`,
          text: m.message,
          time: new Date(m.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          isMe: false,
        }));
        setMessages(mapped);
      }
    } catch {
      // keep existing messages
    }
  }, [supabase]);

  // RBAC-3 — load via /api/rbac/list-qc-inspections (service-role server-side filter).
  // resolveFilter('qc_inspections') sees Project_Manager_Admin as broad-access
  // (added in the consolidation pass), so PM Directors get the full route.
  const loadInspections = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    console.log('[QCInspections] fetching with effectiveRoles=', effectiveRoles, 'effectiveUserId=', effectiveUserId, 'date=', today);

    try {
      const res = await fetchListWithRetry(
        { effectiveRoles, effectiveUserId, date: today },
        controller.signal,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json: { inspections?: QCInspection[]; filterMode?: string } = await res.json();
      setInspections(json.inspections ?? []);
      console.log('[QCInspections] received', json.inspections?.length, 'rows, filterMode=', json.filterMode);
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      const msg = (e as { message?: string })?.message ?? 'Failed to load inspections';
      console.error('[QCInspections] error', msg);
      // Silent fallback — keep whatever state the previous fetch produced.
    } finally {
      setLoading(false);
    }
  }, [effectiveRoles, effectiveUserId, today]);

  // Bootstrap: load inspections → 150ms → photos → 150ms → messages.
  // Sequential with gaps to avoid 429s on cold connections (CLAUDE.md).
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      await loadInspections();
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      await loadPhotos();
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      await loadMessages(selectedInspection?.job_id || '');
    };
    bootstrap();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
    // selectedInspection?.job_id intentionally omitted — bootstrap fires once
    // on identity change, not on every inspection selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadInspections, loadPhotos, loadMessages]);

  // TODO RBAC-3: QC Realtime ignores impersonation and channel-filters by
  // currentUser.id directly — see backlog. Broad-access users also miss live
  // updates for other inspectors' rows due to this channel-level filter.
  useEffect(() => {
    const channel = supabase.
    channel('qc_inspections_realtime').
    on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'qc_inspections',
      filter: `assigned_inspector_id=eq.${currentUser.id}`
    }, (payload) => {
      if (payload.eventType === 'INSERT') {
        setInspections((prev) => [...prev, payload.new as QCInspection]);
      } else if (payload.eventType === 'UPDATE') {
        setInspections((prev) => prev.map((i) => i.id === payload.new.id ? payload.new as QCInspection : i));
      } else if (payload.eventType === 'DELETE') {
        setInspections((prev) => prev.filter((i) => i.id !== payload.old.id));
      }
    }).
    subscribe();

    return () => {supabase.removeChannel(channel);};
  }, [currentUser.id, supabase]);

  const openCustomerCard = useCallback(async (inspection: QCInspection) => {
    setLoadingPipelineCard(true);
    try {
      const nameParts = (inspection.homeowner_name || '').trim().split(' ');
      const lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';
      const firstName = nameParts[0] || '';

      let { data } = await supabase
        .from('customer_pipeline')
        .select('*')
        .ilike('customer_last_name', `%${lastName}%`)
        .limit(5);

      if (!data || data.length === 0) {
        const res = await supabase
          .from('customer_pipeline')
          .select('*')
          .ilike('customer_first_name', `%${firstName}%`)
          .limit(5);
        data = res.data;
      }

      if (!data || data.length === 0) {
        const addressPart = (inspection.address || '').split(',')[0]?.trim();
        if (addressPart) {
          const res = await supabase
            .from('customer_pipeline')
            .select('*')
            .ilike('address', `%${addressPart}%`)
            .limit(5);
          data = res.data;
        }
      }

      if (data && data.length > 0) {
        setSelectedPipelineCard(mapPipelineRow(data[0]));
      } else {
        const res = await supabase.from('customer_pipeline').select('*').limit(1);
        if (res.data && res.data.length > 0) {
          setSelectedPipelineCard(mapPipelineRow(res.data[0]));
        }
      }
    } catch {
      // silently fail
    } finally {
      setLoadingPipelineCard(false);
    }
  }, [supabase]);

  const openDrawer = (inspection: QCInspection) => {
    setSelectedInspection(inspection);
    setDrawerOpen(true);
    loadMessages(inspection.job_id);
    loadPhotos(inspection.job_id);
  };

  const sendMessage = () => {
    if (!chatInput.trim()) return;
    const now = new Date();
    const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const newMsg: ChatMessage = { id: `m${Date.now()}`, sender: 'You', text: chatInput.trim(), time, isMe: true };
    setMessages((prev) => [...prev, newMsg]);
    setChatInput('');

    if (selectedInspection) {
      supabase.from('qc_messages').insert({
        job_id: selectedInspection.job_id,
        sender_name: `${currentUser.first_name} ${currentUser.last_name}`,
        sender_role: (currentUser as any).user_role || 'Project_Manager',
        message: chatInput.trim(),
      }).then(() => {});
    }

    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const pendingCount = inspections.filter((i) => i.status !== 'Completed').length;

  return (
    <section className="space-y-6">
      {/* Hero: Route Map */}
      <div className="bg-white rounded-xl overflow-hidden relative" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: '12px', isolation: 'isolate' }}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Icon name="MapPinIcon" size={18} style={{ color: '#033494' }} variant="solid" />
            <span className="font-heading text-base font-semibold" style={{ color: '#000150' }}>Today&apos;s Inspection Route</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-caption text-xs text-gray-400">{inspections.length} sites mapped</span>
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="font-caption text-xs text-green-500">Live</span>
          </div>
        </div>

        {/* Map Container */}
        <div className="relative" style={{ height: '380px' }}>
          <QCRouteMap inspections={inspections} onPinClick={openDrawer} />

          {/* Floating overlay */}
          <div
            className="absolute top-4 left-4 z-10 bg-white rounded-xl px-4 py-3 flex items-center gap-3"
            style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderLeft: '4px solid #07B0E9' }}>

            <div>
              <p className="font-heading text-xs font-semibold text-gray-500 uppercase tracking-wide">Today&apos;s Route</p>
              <p className="font-heading text-lg font-bold" style={{ color: '#000150' }}>
                {pendingCount} Inspection{pendingCount !== 1 ? 's' : ''} Pending
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}>
              <Icon name="ClipboardDocumentCheckIcon" size={20} className="text-white" variant="solid" />
            </div>
          </div>
        </div>
      </div>

      {/* Inspections Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-base font-bold" style={{ color: '#000150' }}>
            Today&apos;s Inspections
          </h2>
          <div className="flex items-center gap-2">
            <span className="font-caption text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(7,176,233,0.1)', color: '#07B0E9' }}>
              {inspections.filter((i) => i.status === 'Ready for QC').length} Ready
            </span>
            <span className="font-caption text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}>
              {inspections.filter((i) => i.status === 'In Progress').length} In Progress
            </span>
          </div>
        </div>

        {loading ?
        <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3].map((i) =>
          <div key={i} className="bg-white rounded-xl p-5 animate-pulse" style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="h-3 bg-gray-100 rounded w-2/3 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
          )}
          </div> :

        <div className="grid grid-cols-1 gap-4">
            {inspections.map((inspection, idx) =>
          <InspectionCard
            key={inspection.id}
            inspection={inspection}
            index={idx + 1}
            onClick={() => openDrawer(inspection)} />

          )}
          </div>
        }
      </div>

      {/* Slide-out Drawer */}
      {drawerOpen && selectedInspection &&
      <>
          {/* Backdrop */}
          <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          style={{ zIndex: 9998 }}
          onClick={() => setDrawerOpen(false)} />

          {/* Drawer */}
          <div
          className="fixed top-0 right-0 h-full bg-white flex flex-col overflow-hidden"
          style={{
            width: 'min(520px, 100vw)',
            zIndex: 9999,
            boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
            animation: 'qcSlideInRight 0.25s ease-out'
          }}>

            {/* Drawer Header */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100" style={{ background: '#000150' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-caption text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(7,176,233,0.2)', color: '#07B0E9' }}>
                      {selectedInspection.job_id}
                    </span>
                    <StatusBadge status={selectedInspection.status} />
                  </div>
                  <h2 className="font-heading text-base font-bold text-white leading-snug truncate">
                    {selectedInspection.address}
                  </h2>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                  onClick={() => openCustomerCard(selectedInspection)}
                  disabled={loadingPipelineCard}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-body text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: '#07B0E9' }}>
                    {loadingPipelineCard ? (
                      <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Icon name="UserCircleIcon" size={14} variant="solid" />
                    )}
                    Customer Card
                  </button>
                  <button
                  onClick={() => setShowEscalateModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-body text-xs font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: '#EF4444' }}>

                    <Icon name="ExclamationTriangleIcon" size={14} variant="solid" />
                    Escalate Issue
                  </button>
                  <button
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all">

                    <Icon name="XMarkIcon" size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Drawer Body - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Contact Directory */}
              <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="font-heading text-sm font-bold mb-3" style={{ color: '#000150' }}>Contact Directory</h3>
                <div className="grid grid-cols-3 gap-3">
                  <ContactCard
                  role="Homeowner"
                  name={selectedInspection.homeowner_name}
                  phone={selectedInspection.homeowner_phone}
                  icon="HomeIcon"
                  iconColor="#07B0E9"
                  actionIcon="PhoneIcon" />

                  <ContactCard
                  role="Project Manager"
                  name={selectedInspection.pm_name}
                  phone={selectedInspection.pm_phone}
                  icon="BriefcaseIcon"
                  iconColor="#033494"
                  actionIcon="ChatBubbleLeftRightIcon" />

                  <ContactCard
                  role="Crew Foreman"
                  name={selectedInspection.foreman_name}
                  phone={selectedInspection.foreman_phone}
                  icon="WrenchScrewdriverIcon"
                  iconColor="#F97316"
                  actionIcon="PhoneIcon" />

                </div>
              </div>

              {/* Inspection Photos */}
              <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="font-heading text-sm font-bold mb-3" style={{ color: '#000150' }}>Inspection Photos</h3>
                {/* Upload Area */}
                <div
                className="border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-2 mb-4 cursor-pointer hover:border-primary transition-all"
                style={{ borderColor: '#07B0E9', background: 'rgba(7,176,233,0.03)' }}>

                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(7,176,233,0.1)' }}>
                    <Icon name="CameraIcon" size={20} style={{ color: '#07B0E9' }} variant="outline" />
                  </div>
                  <p className="font-body text-sm font-medium" style={{ color: '#07B0E9' }}>Capture / Upload QC Photos</p>
                  <p className="font-caption text-xs text-gray-400">Tap to take photo or select from gallery</p>
                </div>
                {/* Thumbnails */}
                <div className="flex gap-3 flex-wrap">
                  {photos.map((photo) =>
                <div key={photo.id} className="relative group cursor-pointer">
                      <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-24 h-18 object-cover rounded-lg"
                    style={{ width: '96px', height: '72px', borderRadius: '8px' }} />

                      <div className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                        <Icon name="MagnifyingGlassPlusIcon" size={18} className="text-white" />
                      </div>
                    </div>
                )}
                  <div
                  className="w-24 h-18 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-primary transition-all"
                  style={{ width: '96px', height: '72px', borderRadius: '8px' }}>

                    <Icon name="PlusIcon" size={20} className="text-gray-300" />
                  </div>
                </div>
              </div>

              {/* Messaging / Notes */}
              <div className="px-6 py-5">
                <h3 className="font-heading text-sm font-bold mb-3" style={{ color: '#000150' }}>
                  Job Notes & Messaging
                  <span className="ml-2 font-caption text-xs font-normal text-gray-400">tied to {selectedInspection.job_id}</span>
                </h3>
                {/* Chat Messages */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-3" style={{ maxHeight: '200px', overflowY: 'auto', borderRadius: '12px' }}>
                  {messages.map((msg) =>
                <div key={msg.id} className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] ${msg.isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                        {!msg.isMe &&
                    <span className="font-caption text-xs text-gray-400">{msg.sender}</span>
                    }
                        <div
                      className="px-3 py-2 rounded-xl font-body text-sm"
                      style={{
                        background: msg.isMe ? '#07B0E9' : '#fff',
                        color: msg.isMe ? '#fff' : '#000150',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        borderRadius: msg.isMe ? '12px 12px 4px 12px' : '12px 12px 12px 4px'
                      }}>

                          {msg.text}
                        </div>
                        <span className="font-caption text-[10px] text-gray-400">{msg.time}</span>
                      </div>
                    </div>
                )}
                  <div ref={chatEndRef} />
                </div>
                {/* Input */}
                <div className="flex gap-2">
                  <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type a note or message to PM/Crew..."
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 font-body text-sm focus:outline-none focus:border-primary transition-all"
                  style={{ borderRadius: '12px' }}
                  suppressHydrationWarning />

                  <button
                  onClick={sendMessage}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white transition-all hover:opacity-90"
                  style={{ background: '#07B0E9', borderRadius: '12px' }}>

                    <Icon name="PaperAirplaneIcon" size={16} variant="solid" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      }

      {/* Unified Customer Card Panel */}
      {selectedPipelineCard && (
        <CustomerCardPanel
          card={selectedPipelineCard}
          onClose={() => setSelectedPipelineCard(null)}
        />
      )}

      {/* Escalate Modal */}
      {showEscalateModal && selectedInspection &&
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowEscalateModal(false)} />
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
                <Icon name="ExclamationTriangleIcon" size={20} style={{ color: '#EF4444' }} variant="solid" />
              </div>
              <div>
                <h3 className="font-heading text-base font-bold" style={{ color: '#000150' }}>Escalate Issue</h3>
                <p className="font-caption text-xs text-gray-400">{selectedInspection.job_id} · {selectedInspection.address.split(',')[0]}</p>
              </div>
            </div>
            <textarea
            value={escalateNote}
            onChange={(e) => setEscalateNote(e.target.value)}
            placeholder="Describe the issue that needs escalation..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm resize-none focus:outline-none focus:border-red-400 transition-all mb-4"
            style={{ borderRadius: '12px' }}
            suppressHydrationWarning />

            <div className="flex gap-3">
              <button
              onClick={() => setShowEscalateModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 font-body text-sm text-gray-600 hover:bg-gray-50 transition-all"
              style={{ borderRadius: '12px' }}>

                Cancel
              </button>
              <button
              onClick={() => {setShowEscalateModal(false);setEscalateNote('');}}
              className="flex-1 py-2.5 rounded-xl font-body text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: '#EF4444', borderRadius: '12px' }}>

                Submit Escalation
              </button>
            </div>
          </div>
        </div>
      }

      <style jsx global>{`
        @keyframes qcSlideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </section>);

}

// --- Sub-components ---

function InspectionCard({ inspection, index, onClick }: {inspection: QCInspection;index: number;onClick: () => void;}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 group cursor-pointer"
      style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)', minHeight: '44px' }}>

      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center font-heading text-xs font-bold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}>

            {index}
          </div>
          <span className="font-heading text-sm font-bold" style={{ color: '#033494' }}>{inspection.job_id}</span>
        </div>
        <StatusBadge status={inspection.status} />
      </div>

      <div className="flex items-start gap-2 mb-3">
        <Icon name="MapPinIcon" size={14} className="text-gray-400 mt-0.5 flex-shrink-0" variant="outline" />
        <p className="font-body text-sm text-gray-600 leading-snug">{inspection.address}</p>
      </div>

      {inspection.notes &&
      <div className="bg-amber-50 rounded-lg px-3 py-2 mb-3">
          <p className="font-caption text-xs text-amber-700 line-clamp-2">{inspection.notes}</p>
        </div>
      }

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon name="UserIcon" size={12} className="text-gray-400" variant="outline" />
          <span className="font-caption text-[10px] text-gray-400">{inspection.homeowner_name.split('&')[0].trim()}</span>
        </div>
        <div className="flex items-center gap-1 text-primary opacity-0 group-hover:opacity-100 transition-all">
          <span className="font-caption text-xs" style={{ color: '#07B0E9' }}>View Details</span>
          <Icon name="ChevronRightIcon" size={12} style={{ color: '#07B0E9' }} />
        </div>
      </div>
    </button>);

}

function StatusBadge({ status }: {status: string;}) {
  const styles: Record<string, {bg: string;color: string;}> = {
    'Ready for QC': { bg: 'rgba(7,176,233,0.1)', color: '#07B0E9' },
    'In Progress': { bg: 'rgba(249,115,22,0.1)', color: '#F97316' },
    'Completed': { bg: 'rgba(16,185,129,0.1)', color: '#10B981' }
  };
  const s = styles[status] || styles['Ready for QC'];
  return (
    <span
      className="font-caption text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
      style={{ background: s.bg, color: s.color }}>

      {status}
    </span>);

}

function ContactCard({
  role, name, phone, icon, iconColor, actionIcon
}: {role: string;name: string;phone: string;icon: string;iconColor: string;actionIcon: string;}) {
  return (
    <div
      className="bg-gray-50 rounded-xl p-3 flex flex-col gap-2"
      style={{ borderRadius: '10px' }}>

      <div className="flex items-center gap-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${iconColor}18` }}>

          <Icon name={icon as any} size={14} style={{ color: iconColor }} variant="outline" />
        </div>
        <span className="font-caption text-[10px] font-semibold text-gray-400 uppercase tracking-wide leading-tight">{role}</span>
      </div>
      <div>
        <p className="font-body text-xs font-semibold leading-tight" style={{ color: '#000150' }}>{name}</p>
        <p className="font-caption text-[11px] text-gray-400 mt-0.5">{phone}</p>
      </div>
      <button
        className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-body text-sm font-semibold text-white transition-all hover:opacity-90"
        style={{ background: iconColor, minHeight: '44px' }}>

        <Icon name={actionIcon as any} size={16} variant="solid" />
        {actionIcon === 'PhoneIcon' ? 'Call' : 'Message'}
      </button>
    </div>);

}
